import { SetStateAction, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';

type ExpireType = Date | number | null | undefined;

interface StoredValue<T> {
    value: T;
    expire: ExpireType;
}

const DEFAULT_EXPIRE = 48 * 60 * 60 * 1000; // 2 days

export function useExpireLocalStorage<T = undefined>(
    key: string,
    initialValue: T,
    expire: ExpireType = null
): [T, (value: SetStateAction<T>, newExpire?: ExpireType) => void] {
    const keyWithExpire = `expirable--${key}`;

    const getExpiration = (customExpire: ExpireType) => {
        if (customExpire === undefined || customExpire === null) {
            return Date.now() + DEFAULT_EXPIRE;
        }
        return customExpire instanceof Date ? customExpire.getTime() : Date.now() + customExpire;
    };

    const [storedValue, setStoredValue] = useLocalStorage<StoredValue<T>>(keyWithExpire, {
        value: initialValue,
        expire: getExpiration(expire),
    });

    const preparedValue = useMemo(() => {
        if (storedValue.expire && Date.now() > Number(storedValue.expire)) {
            try {
                window.localStorage.removeItem(keyWithExpire);
            } catch (error) {
                console.error(`Error removing item from localStorage: ${error}`);
            }
        }
        return storedValue.value;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedValue.expire, storedValue.value]);

    const setExpireValue = (value: SetStateAction<T>, newExpire: ExpireType = null) => {
        const valueToStore = value instanceof Function ? value(storedValue.value) : value;
        setStoredValue({
            value: valueToStore,
            expire: newExpire ? getExpiration(newExpire) : getExpiration(expire),
        });
    };

    return [preparedValue, setExpireValue];
}
